/**
 * Pure rules for recognising and governing a voice note on a comment.
 *
 * Kept out of the components so they can be tested without mounting anything,
 * and so the comment and reply render paths — which are separate, duplicated
 * blocks in activityDetailCard — cannot drift apart on the question of what
 * counts as a voice note.
 */

import { COMMENT_EDIT_WINDOW_MS } from './appConstants';

// Must match constants.ATTACHMENT_KIND_VOICE_NOTE on the backend.
export const VOICE_NOTE_KIND = 'voiceNote';

// Deleting a voice note shares the edit window deliberately — one rule for
// "changing what you said" — so this reuses that constant rather than
// declaring a second one that could drift from it. The server is the
// authority either way; this only decides whether to render the button, and a
// stale UI still gets a clean 422 rather than a broken delete.
export const VOICE_NOTE_DELETE_WINDOW_MS = COMMENT_EDIT_WINDOW_MS;

/**
 * `m:ss` for both the recorder's running clock and the player's transport, so
 * the same recording never reads as two different lengths in the two halves of
 * the feature.
 */
export const formatDuration = (totalSeconds) => {
  const safe = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

/**
 * `kind` is what the backend writes and the only reliable answer. The mimeType
 * and extension fallbacks exist for an audio file attached some other way, so
 * it still gets a player rather than a broken <img>.
 */
export const isVoiceNoteAttachment = (attachment) => {
  if (!attachment) return false;
  if (attachment.kind === VOICE_NOTE_KIND) return true;
  if (String(attachment.mimeType || '').startsWith('audio/')) return true;
  return /\.(webm|m4a|mp3|ogg|wav|weba|aac)$/i.test(attachment.originalFileName || '');
};

// A recording made in the browser has no name of its own, so one is built from
// what it actually is. Keyed on the containers `pickRecordingMimeType` can
// choose plus the formats an audio file attached some other way arrives in.
const MIME_EXTENSIONS = {
  'audio/webm': 'webm',
  'audio/mp4': 'm4a',
  'audio/mpeg': 'mp3',
  'audio/ogg': 'ogg',
  'audio/wav': 'wav',
  'audio/aac': 'aac'
};

/**
 * What a downloaded voice note is called on disk. The name the file was stored
 * under wins; failing that the container decides the extension, because a
 * `.webm` saved as `.mp3` is a file the operating system refuses to open.
 */
export const voiceNoteFileName = (attachment) => {
  const given = String(attachment?.originalFileName ?? '').trim();
  if (given) return given;

  const container = String(attachment?.mimeType ?? '')
    .split(';')[0]
    .trim()
    .toLowerCase();

  return `voice-note.${MIME_EXTENSIONS[container] || 'webm'}`;
};

/**
 * Author only, inside the hour — the same two rules the backend enforces.
 *
 * An unparseable createdAt counts as expired, matching the server: "too late"
 * is the safe way to be wrong, never "deletable forever".
 */
export const canDeleteVoiceNote = (comment, currentUserId, now = Date.now()) => {
  if (!comment || !currentUserId) return false;
  if (String(comment.userId ?? '') !== String(currentUserId)) return false;

  const postedAt = new Date(comment.createdAt ?? comment.time ?? NaN).getTime();
  if (!Number.isFinite(postedAt)) return false;

  return now - postedAt < VOICE_NOTE_DELETE_WINDOW_MS;
};
